import { SortInput } from '@apeiron/common'
import {
  PlanetCardInfoType,
  PlanetFilterInput,
  PlanetLite,
  PlanetSelectSortItems,
  PlanetSortBy
} from '@apeiron/planet'
import {
  ApeironBlueButton,
  INFO_POPUP_TYPE,
  InfoPopup,
  noForwardProps,
  Pagination,
  useAccountContext,
  useMessageContext
} from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import ListTemplate from '@src/components/layout/ListTemplate'
import InventoryPlanetThirdNavBar from '@src/components/navigationBar/third/InventoryPlanetThirdNavBar'
import PlanetFilter from '@src/components/planet/filter/PlanetFilter'
import { PlanetList } from '@src/components/planet/list/PlanetList'
import ApeironSelectExceedMessage from '@src/components/share/apeiron/ApeironSelectExceedMessage'
import ShadowScroll from '@src/components/share/generic/ShadowScroll'
import SortDropdown from '@src/components/share/generic/SortDropdown'
import { DEFAULT_MESSAGE_SHOW_DURATION } from '@src/constants/layout'
import { ROUTE } from '@src/constants/route'
import { PlanetSkillsProvider } from '@src/contexts/planet/PlanetSkillsContext'
import { PlanetTypeProvider } from '@src/contexts/planet/PlanetTypeContext'
import useGetMyPlanets, {
  GetMyPlanetsRequest
} from '@src/hooks/graphql/useGetMyPlanets'
import useGetMySeedPlanets from '@src/hooks/graphql/useGetMySeedPlanets'
import { PlanetLiteDeserializeOption } from '@src/types/deserialize'
import * as R from 'ramda'
import {
  FC,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import { Trans, useTranslation } from 'react-i18next'

enum NavigationSelection {
  NFT = 0,
  SEED
}

const StyledInfoPopup = styled(InfoPopup)`
  height: 1200px;
  max-height: 95vh;
  max-width: 95vw;
  width: 1500px;

  & > .Custom-Content {
    height: calc(100% - 50px);
  }
`

const Total = styled(Typography)`
  align-self: flex-start;
  color: white;
  font-size: 24px;
  line-height: 28px;
  ${props => props.theme.breakpoints.down('res1024')} {
    margin-left: 4%;
    margin-bottom: 10px;
  }
`

const ListContainer = styled(ShadowScroll)`
  flex: 1;
  height: 100%;
`

const StyledPlanetList = styled(PlanetList)`
  align-items: center;
  padding-top: 10px;
`

const PageMainContainer = styled(Box)`
  display: flex;
  position: relative;
  width: 100%;
  flex-direction: column;
`

const PageContainer = styled(Box, noForwardProps)<PageContainerProps>`
  display: flex;
  position: relative;
  width: 100%;

  ${props => props.theme.breakpoints.between('res480', 'res768')} {
    flex-wrap: wrap;
    justify-content: end;
    row-gap: 10px;
    > :first-of-type {
      width: 100%;
    }
  }

  ${props => props.theme.breakpoints.up('res768')} {
    ${props =>
      props.__multiselect
        ? `
          flex-wrap: wrap;
          align-items: center;
          gap: 10px;
          justify-content: space-between;
          padding: 0px 15px;
        `
        : `
          justify-content: center;
          padding: 0px 15px;
        `}
  }

  ${props => props.theme.breakpoints.down('res480')} {
    ${props =>
      props.__multiselect
        ? `
        flex-direction: column;
        gap: 10px;
        padding: 10px;
      `
        : `
        height: 80px;
        justify-content: center;
        padding: 0px 15px;
      `}
  }
`

const ConfirmButton = styled(ApeironBlueButton)`
  ${props => props.theme.breakpoints.up('res480')} {
    width: 200px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    align-self: center;
    width: 100%;
    max-width: 360px;
  }
`

const FilterSidePanel = styled(PlanetFilter)`
  display: flex;
  flex-direction: column;
  overflow: auto;
  width: 100%;
`

const StyledSortDropdown = styled(SortDropdown<PlanetSortBy>)`
  margin-right: 12px;
  width: 200px;
`

const BottomContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`

const MultiSelectText = styled(Typography)`
  margin-left: auto;
  margin-right: 15px;
  color: #999999;
  strong {
    color: #f1b535;
  }
`

const Content: FC<ContentProps> = props => {
  const {
    allowSeedPlanet = false,
    cardInfoType,
    listRef,
    open,
    preselectedPlanets,
    selectLimit = 1,
    selectLimitExceedMessage,
    deriveFetchParams,
    disablePlanet,
    onPostProcess,
    onSelect
  } = props

  const multiselect = selectLimit > 1

  const { t } = useTranslation()

  const { isLoggedIn } = useAccountContext()

  const { setInfo } = useMessageContext()

  // only set in "selectLimit > 1"
  const [selectedPlanets, setSelectedPlanets] = useState<PlanetLite[]>([])

  const [filterInput, setFilterInput] = useState<PlanetFilterInput>({})

  const [sortInput, setSortInput] = useState<SortInput<PlanetSortBy>>({
    sortBy: PlanetSortBy.PlanetIdDesc
  })

  const [navigationIndex, setNavigationIndex] = useState<number>(
    NavigationSelection.NFT
  )

  const showingNFT = navigationIndex === NavigationSelection.NFT

  const fetchMyPlanetsHook = useGetMyPlanets()

  const fetchMySeedPlanetsHook = useGetMySeedPlanets()

  const fetchHook = showingNFT ? fetchMyPlanetsHook : fetchMySeedPlanetsHook

  const { data, loading, pageInfo, fetch } = fetchHook

  const [currentPage, setCurrentPage] = useState(fetchHook.pageInfo.page)

  const planets = useMemo(() => {
    return R.map((planet: PlanetLite): PlanetLite => {
      let resultPlanet = planet

      // mark selected
      if (
        R.findIndex(
          R.propEq(resultPlanet.planetID, 'planetID'),
          selectedPlanets
        ) !== -1
      ) {
        resultPlanet = {
          ...resultPlanet,
          selected: true,
          bottomStatus: { translationKey: 'common.selected' }
        }
      }

      if (disablePlanet) {
        const translationKey = disablePlanet(planet)

        // mark disabled
        if (!R.isNil(translationKey) && !R.isEmpty(translationKey)) {
          resultPlanet = {
            ...resultPlanet,
            disabled: true,
            bottomStatus: { translationKey }
          }
        }
      }

      return resultPlanet
    }, data)
  }, [disablePlanet, data, selectedPlanets])

  const handleOnClick = useCallback(
    (planet: PlanetLite) => {
      if (selectLimit > 1) {
        const index = R.findIndex(
          R.propEq(planet.planetID, 'planetID'),
          selectedPlanets
        )

        const newSelected =
          index === -1
            ? R.append(planet, selectedPlanets)
            : R.remove(index, 1, selectedPlanets)

        if (R.length(newSelected) <= selectLimit) {
          setSelectedPlanets(newSelected)
        } else {
          setInfo({
            message: selectLimitExceedMessage || (
              <ApeironSelectExceedMessage selectLimit={selectLimit} />
            ),
            skipTouch: true,
            timeout: DEFAULT_MESSAGE_SHOW_DURATION
          })
        }
      } else {
        onSelect([planet])
      }
    },
    [selectLimit, selectedPlanets, selectLimitExceedMessage, setInfo, onSelect]
  )

  const handleOnMultiselectConfirm = useCallback(() => {
    onSelect(selectedPlanets)
  }, [onSelect, selectedPlanets])

  const handleOnStatusClick = useCallback((planet: PlanetLite) => {
    window.open(
      `${ROUTE.MARKETPLACE_PLANET_DETAIL}?planetId=${planet.planetID}`,
      '_blank'
    )
  }, [])

  const getPlanets = useCallback(
    async (
      filterInput: PlanetFilterInput,
      sortInput: SortInput<PlanetSortBy>,
      page: number
    ) => {
      const [request, option] = await deriveFetchParams()

      await fetch(
        {
          ...request,
          pageInput: { page },
          filterInput,
          sortInput
        },
        option
      )
    },
    [deriveFetchParams, fetch]
  )

  const handleOnPreviousPage = useCallback(() => {
    setCurrentPage(currentPage - 1)
  }, [currentPage])

  const handleOnNextPage = useCallback(() => {
    setCurrentPage(currentPage + 1)
  }, [currentPage])

  const handleOnSubmitPage = useCallback(
    (page: number) => {
      getPlanets(filterInput, sortInput, page)
    },
    [filterInput, getPlanets, sortInput]
  )

  const handleOnRightClickURL = useCallback((planet: PlanetLite) => {
    return planet.seed
      ? ''
      : `${ROUTE.MARKETPLACE_PLANET_DETAIL}?planetId=${planet.planetID}`
  }, [])

  const handleOnNavBarChange = useCallback((index: number) => {
    setNavigationIndex(index)
  }, [])

  const onFilterChange = useCallback((input: PlanetFilterInput) => {
    setFilterInput(input)

    setCurrentPage(1)
  }, [])

  const onSortChange = useCallback((input: SortInput<PlanetSortBy>) => {
    setSortInput(input as SortInput<PlanetSortBy>)
    setCurrentPage(1)
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedPlanets([])
  }, [])

  useImperativeHandle(
    listRef,
    () => {
      return {
        clearSelection
      }
    },
    [clearSelection]
  )

  useEffect(() => {
    if (open) {
      getPlanets(filterInput, sortInput, currentPage)
    }
  }, [currentPage, filterInput, sortInput, navigationIndex, open])

  useEffect(() => {
    if (!R.isNil(preselectedPlanets)) {
      setSelectedPlanets(preselectedPlanets)
    }
  }, [preselectedPlanets])

  return (
    <PlanetTypeProvider>
      <PlanetSkillsProvider>
        <ListTemplate
          thirdNavBar={
            allowSeedPlanet ? (
              <InventoryPlanetThirdNavBar
                isSelection
                index={navigationIndex}
                onChange={handleOnNavBarChange}
              />
            ) : null
          }
          popupMode
          filter={
            showingNFT ? (
              <FilterSidePanel
                allowSave={false}
                isLoggedIn={isLoggedIn}
                filterInput={filterInput}
                sortInput={sortInput}
                onFilterInputChange={onFilterChange}
              />
            ) : null
          }
          sortDropDown={
            showingNFT ? (
              <StyledSortDropdown
                items={PlanetSelectSortItems}
                onSelectChange={onSortChange}
                value={sortInput}
              />
            ) : null
          }
          loadingContent={loading}
          mainContent={
            <ListContainer>
              <StyledPlanetList
                cardInfoType={cardInfoType}
                emptyStateDescription={t('planet.list.empty_on_select')}
                enableLink={false}
                onItemClick={handleOnClick}
                onPlanetStatusClick={handleOnStatusClick}
                onPostProcess={onPostProcess}
                planets={planets}
                renderPlanetUrl={handleOnRightClickURL}
              />
            </ListContainer>
          }
          headerLeft={
            <Total>
              {t(
                showingNFT ? 'planet.list.nft.total' : 'planet.list.seed.total',
                { count: loading ? 0 : pageInfo.total }
              )}
            </Total>
          }
          bottomBar={
            <BottomContainer>
              {pageInfo.totalPage > 0 && (
                <PageMainContainer>
                  <PageContainer __multiselect={multiselect}>
                    <Pagination
                      prevDisabled={pageInfo.page === 1}
                      nextDisabled={R.isNil(pageInfo.nextCursor)}
                      onPrev={handleOnPreviousPage}
                      onNext={handleOnNextPage}
                      onSubmit={handleOnSubmitPage}
                      value={pageInfo.page}
                      totalPage={pageInfo.totalPage}
                    />

                    {multiselect && (
                      <ConfirmButton
                        disabled={R.isEmpty(selectedPlanets)}
                        onClick={handleOnMultiselectConfirm}
                      >
                        {t('common.button.confirm')}
                        {!R.isEmpty(selectedPlanets) &&
                          ` (${R.length(selectedPlanets)})`}
                      </ConfirmButton>
                    )}
                  </PageContainer>
                  {multiselect && (
                    <MultiSelectText>
                      <Trans>
                        {t('pagination.multi_select_limit', {
                          count: selectLimit
                        })}
                      </Trans>
                    </MultiSelectText>
                  )}
                </PageMainContainer>
              )}
            </BottomContainer>
          }
        />
      </PlanetSkillsProvider>
    </PlanetTypeProvider>
  )
}

const PlanetSelectPopup: FC<Props> = (props: Props) => {
  const { open, onClose, onSelect, ...rest } = props

  const ref = useRef<ListRef | null>(null)

  const handleOnClose = useCallback(() => {
    if (ref.current) {
      ref.current.clearSelection()
    }

    if (onClose) {
      onClose()
    }
  }, [onClose])

  const handleOnSelect = useCallback(
    (planets: PlanetLite[]) => {
      if (onSelect) {
        onSelect(planets)
      }

      if (ref.current) {
        ref.current.clearSelection()
      }
    },
    [onSelect]
  )

  return (
    <StyledInfoPopup
      type={INFO_POPUP_TYPE.BLACK}
      open={open}
      keepMounted
      onClose={handleOnClose}
    >
      <Content open={open} listRef={ref} onSelect={handleOnSelect} {...rest} />
    </StyledInfoPopup>
  )
}

export type FetchParamsCallback = () => Promise<
  [GetMyPlanetsRequest, PlanetLiteDeserializeOption | undefined]
>

type PageContainerProps = {
  __multiselect?: boolean
}

type ListRef = {
  clearSelection: () => void
}

type ContentProps = {
  allowSeedPlanet?: boolean
  cardInfoType?: PlanetCardInfoType
  listRef?: Ref<ListRef | null>
  open: boolean
  preselectedPlanets?: PlanetLite[]
  selectLimit?: number
  selectLimitExceedMessage?: ReactNode
  deriveFetchParams: FetchParamsCallback
  disablePlanet?: (planet: PlanetLite) => string | null
  onPostProcess?: (planet: PlanetLite) => PlanetLite
  onSelect: (planets: PlanetLite[]) => void
}

type Props = ContentProps & {
  onClose?: () => void
}

export default PlanetSelectPopup
